import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAlumnos, getAsistencia, bulkCreateAsistencia, updateAsistencia, darBajaAlumno } from '../lib/api';
import { formatNombreCompleto } from '../lib/nombre';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  CalendarBlank, Check, X, Clock, FirstAidKit, FloppyDisk,
  Warning, CalendarCheck, UserMinus
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const AsistenciaPage = () => {
  const { user, hasPermission } = useAuth();
  const [date, setDate] = useState(new Date());
  const [alumnos, setAlumnos] = useState([]);
  const [asistencia, setAsistencia] = useState({});
  const [existingRecords, setExistingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    grado: '',
    grupo: '',
    turno: user?.turno || ''
  });
  const [showJustifyModal, setShowJustifyModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [justificacion, setJustificacion] = useState('');
  const [showBajaModal, setShowBajaModal] = useState(false);
  const [bajaAlumno, setBajaAlumno] = useState(null);
  const [bajaForm, setBajaForm] = useState({ tipo_baja: 'baja_temporal', motivo: '' });
  const [bajaLoading, setBajaLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Fetch students
      const params = { limit: 200 };
      if (filters.grado && filters.grado !== 'all') params.grado = filters.grado;
      if (filters.grupo && filters.grupo !== 'all') params.grupo = filters.grupo;
      if (filters.turno && filters.turno !== 'all') params.turno = filters.turno;
      
      const [alumnosRes, asistenciaRes] = await Promise.all([
        getAlumnos(params),
        getAsistencia({ fecha: dateStr, ...params })
      ]);
      
      setAlumnos(alumnosRes.data.alumnos.filter(a => a.estatus === 'activo'));
      const asistenciaList = Array.isArray(asistenciaRes.data)
        ? asistenciaRes.data
        : (asistenciaRes.data?.asistencias || []);
      setExistingRecords(asistenciaList);
      
      // Build asistencia state from existing records
      const asistenciaMap = {};
      asistenciaList.forEach(record => {
        asistenciaMap[record.alumno_id] = {
          id: record.id,
          estado: record.estado,
          justificacion: record.justificacion
        };
      });
      setAsistencia(asistenciaMap);
      
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [date, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEstadoChange = (alumnoId, estado) => {
    const existingRecord = asistencia[alumnoId];
    // If record already exists in DB → update via PUT (immediate save)
    if (existingRecord?.id) {
      updateAsistencia(existingRecord.id, estado)
        .then(() => {
          toast.success('Asistencia actualizada');
          fetchData();
        })
        .catch((error) => {
          toast.error(error.response?.data?.detail || 'Error al actualizar');
        });
      return;
    }
    // Otherwise, stage the new record locally to be saved with "Guardar"
    setAsistencia(prev => ({
      ...prev,
      [alumnoId]: { ...prev[alumnoId], estado, isNew: true }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Filter new records only
      const newRecords = alumnos
        .filter(a => asistencia[a.id]?.isNew && asistencia[a.id]?.estado)
        .map(a => ({
          alumno_id: a.id,
          matricula: a.matricula,
          estado: asistencia[a.id].estado
        }));
      
      if (newRecords.length > 0) {
        const resp = await bulkCreateAsistencia({ fecha: dateStr, asistencias: newRecords });
        const created = resp.data?.created ?? newRecords.length;
        const errores = resp.data?.errors || [];
        if (errores.length > 0) {
          // Algunos registros fallaron en el backend; el usuario debe enterarse para no asumir
          // que se guardó todo.
          toast.warning(
            `Se guardaron ${created} de ${newRecords.length} registros. ${errores.length} fallaron:\n` +
              errores.slice(0, 3).join('\n') +
              (errores.length > 3 ? `\n... y ${errores.length - 3} más` : ''),
            { duration: 8000 }
          );
        } else {
          toast.success(`${created} registros guardados`);
        }
        fetchData();
      } else {
        toast.info('No hay cambios pendientes');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleJustify = async () => {
    if (!selectedRecord) return;
    try {
      await updateAsistencia(selectedRecord.id, 'justificado', justificacion);
      toast.success('Asistencia justificada');
      setShowJustifyModal(false);
      setJustificacion('');
      fetchData();
    } catch (error) {
      toast.error('Error al justificar');
    }
  };

  const openJustifyModal = (record) => {
    setSelectedRecord(record);
    setJustificacion(record.justificacion || '');
    setShowJustifyModal(true);
  };

  const openBajaModal = (alumno) => {
    setBajaAlumno(alumno);
    setBajaForm({ tipo_baja: 'baja_temporal', motivo: '' });
    setShowBajaModal(true);
  };

  const handleBaja = async () => {
    if (!bajaAlumno) return;
    if (!bajaForm.motivo || bajaForm.motivo.trim().length < 5) {
      toast.error('El motivo es obligatorio (mínimo 5 caracteres)');
      return;
    }
    setBajaLoading(true);
    try {
      await darBajaAlumno(bajaAlumno.id, bajaForm.motivo.trim(), bajaForm.tipo_baja);
      toast.success(`${formatNombreCompleto(bajaAlumno)} dado de baja. Se generó la incidencia automáticamente.`);
      setShowBajaModal(false);
      setBajaAlumno(null);
      setBajaForm({ tipo_baja: 'baja_temporal', motivo: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al dar de baja');
    } finally {
      setBajaLoading(false);
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'asistencia':
        return <Check size={18} className="text-green-600" weight="bold" />;
      case 'falta':
        return <X size={18} className="text-red-600" weight="bold" />;
      case 'retardo':
        return <Clock size={18} className="text-amber-600" weight="bold" />;
      case 'justificado':
        return <FirstAidKit size={18} className="text-blue-600" weight="bold" />;
      default:
        return null;
    }
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      asistencia: 'bg-green-100 text-green-800',
      falta: 'bg-red-100 text-red-800',
      retardo: 'bg-amber-100 text-amber-800',
      justificado: 'bg-blue-100 text-blue-800'
    };
    return styles[estado] || 'bg-gray-100 text-gray-800';
  };

  const countByEstado = (estado) => {
    return Object.values(asistencia).filter(a => a.estado === estado).length;
  };

  const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  // Días naturales transcurridos desde la fecha seleccionada
  const daysSince = Math.round((new Date(format(new Date(), 'yyyy-MM-dd')) - new Date(format(date, 'yyyy-MM-dd'))) / 86400000);
  const within7Days = daysSince >= 0 && daysSince <= 7;
  // Admin & Direccion can edit any date. Prefectura only the same day.
  const canEdit = hasPermission(['administrador', 'direccion']) || (hasPermission(['prefectura']) && isToday);
  // Justificar: Admin/Direccion sin límite. Prefectura hasta 7 días naturales atrás.
  const canJustify = hasPermission(['administrador', 'direccion']) || (hasPermission(['prefectura']) && within7Days);
  // Admin, Direccion and Prefectura can mark students as "no longer attending" (baja)
  const canMarkBaja = hasPermission(['administrador', 'direccion', 'prefectura']);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="asistencia-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Asistencia</h1>
          <p className="text-muted-foreground">Registro de asistencia diaria</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start" data-testid="date-picker">
                <CalendarBlank size={16} className="mr-2" />
                {format(date, "d 'de' MMMM, yyyy", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                locale={es}
              />
            </PopoverContent>
          </Popover>
          
          {canEdit && (
            <Button onClick={handleSave} disabled={saving} data-testid="save-asistencia">
              <FloppyDisk size={16} className="mr-2" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Check size={24} className="text-green-600" weight="bold" />
            <div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{countByEstado('asistencia')}</p>
              <p className="text-sm text-green-600 dark:text-green-400">Asistencias</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800">
          <CardContent className="p-4 flex items-center gap-3">
            <X size={24} className="text-red-600" weight="bold" />
            <div>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{countByEstado('falta')}</p>
              <p className="text-sm text-red-600 dark:text-red-400">Faltas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock size={24} className="text-amber-600" weight="bold" />
            <div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{countByEstado('retardo')}</p>
              <p className="text-sm text-amber-600 dark:text-amber-400">Retardos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
          <CardContent className="p-4 flex items-center gap-3">
            <FirstAidKit size={24} className="text-blue-600" weight="bold" />
            <div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{countByEstado('justificado')}</p>
              <p className="text-sm text-blue-600 dark:text-blue-400">Justificados</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Grado</Label>
              <Select value={filters.grado} onValueChange={(v) => setFilters(p => ({ ...p, grado: v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="1">1°</SelectItem>
                  <SelectItem value="2">2°</SelectItem>
                  <SelectItem value="3">3°</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Grupo</Label>
              <Select value={filters.grupo} onValueChange={(v) => setFilters(p => ({ ...p, grupo: v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Turno</Label>
              <Select 
                value={filters.turno} 
                onValueChange={(v) => setFilters(p => ({ ...p, turno: v }))}
                disabled={user?.rol === 'prefectura' && user?.turno}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="matutino">Matutino</SelectItem>
                  <SelectItem value="vespertino">Vespertino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilters({ grado: '', grupo: '', turno: user?.turno || '' })}
                className="w-full"
              >
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <CalendarCheck size={20} />
            Lista de Asistencia - {format(date, "EEEE d 'de' MMMM", { locale: es })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[100px]">Matrícula</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-[80px]">Grado</TableHead>
                  <TableHead className="w-[80px]">Grupo</TableHead>
                  <TableHead className="w-[100px]">Turno</TableHead>
                  <TableHead className="w-[300px] text-center">Asistencia</TableHead>
                  <TableHead className="w-[120px]">Estado</TableHead>
                  <TableHead className="w-[80px] text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : alumnos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No se encontraron alumnos con los filtros seleccionados
                    </TableCell>
                  </TableRow>
                ) : (
                  alumnos.map((alumno) => {
                    const record = asistencia[alumno.id];
                    const hasRecord = !!record?.id;
                    const isJustificado = record?.estado === 'justificado';

                    return (
                      <TableRow key={alumno.id} className="hover:bg-muted/30">
                        <TableCell className="font-mono text-sm">{alumno.matricula}</TableCell>
                        <TableCell className="font-medium min-w-[160px] max-w-[260px]">
                          <div className="flex items-start gap-2">
                            <span
                              className="text-xs sm:text-sm md:text-base break-words whitespace-normal leading-tight"
                              title={formatNombreCompleto(alumno)}
                            >
                              {formatNombreCompleto(alumno)}
                            </span>
                            {alumno.en_patre && (
                              <Warning size={14} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{alumno.grado}°</TableCell>
                        <TableCell>{alumno.grupo}</TableCell>
                        <TableCell className="capitalize">{alumno.turno}</TableCell>
                        <TableCell>
                          <div className="flex justify-center items-center gap-2">
                            {canEdit ? (
                              <>
                                <Button
                                  size="sm"
                                  variant={record?.estado === 'asistencia' ? 'default' : 'outline'}
                                  className={record?.estado === 'asistencia' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                                  onClick={() => handleEstadoChange(alumno.id, 'asistencia')}
                                  disabled={isJustificado}
                                  data-testid={`asistencia-${alumno.matricula}`}
                                  title="Marcar asistencia"
                                >
                                  <Check size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant={record?.estado === 'retardo' ? 'default' : 'outline'}
                                  className={record?.estado === 'retardo' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                                  onClick={() => handleEstadoChange(alumno.id, 'retardo')}
                                  disabled={isJustificado}
                                  data-testid={`retardo-${alumno.matricula}`}
                                  title="Marcar retardo"
                                >
                                  <Clock size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant={record?.estado === 'falta' ? 'default' : 'outline'}
                                  className={record?.estado === 'falta' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                                  onClick={() => handleEstadoChange(alumno.id, 'falta')}
                                  disabled={isJustificado}
                                  data-testid={`falta-${alumno.matricula}`}
                                  title="Marcar falta"
                                >
                                  <X size={16} />
                                </Button>
                                {/* Justify icon: visible after falta/retardo for admin/direccion/prefectura */}
                                {hasRecord && (record.estado === 'falta' || record.estado === 'retardo') && canJustify && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => openJustifyModal(record)}
                                    data-testid={`justify-${alumno.matricula}`}
                                    title="Justificar inasistencia"
                                  >
                                    <FirstAidKit size={16} weight="bold" />
                                  </Button>
                                )}
                                {hasRecord && isJustificado && (
                                  <FirstAidKit size={18} className="text-blue-600" weight="fill" />
                                )}
                              </>
                            ) : hasRecord ? (
                              <div className="flex items-center gap-2">
                                {getEstadoIcon(record.estado)}
                                <span className="text-xs text-muted-foreground capitalize">{record.estado}</span>
                                {(record.estado === 'falta' || record.estado === 'retardo') && canJustify && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => openJustifyModal(record)}
                                    data-testid={`justify-${alumno.matricula}`}
                                    title="Justificar inasistencia (hasta 7 días naturales)"
                                  >
                                    <FirstAidKit size={16} weight="bold" />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">Sin registro</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {record?.estado && (
                            <Badge className={`text-xs ${getEstadoBadge(record.estado)}`}>
                              {record.estado}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {canMarkBaja && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-700 hover:text-red-800 hover:bg-red-50"
                              onClick={() => openBajaModal(alumno)}
                              data-testid={`baja-${alumno.matricula}`}
                              title="Marcar como ya no asiste (baja)"
                            >
                              <UserMinus size={18} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Justify Modal */}
      <Dialog open={showJustifyModal} onOpenChange={setShowJustifyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Justificar Inasistencia</DialogTitle>
            <DialogDescription>
              Captura el motivo de la justificación (médica, familiar, etc.). Al guardar, el estado del registro pasará a "justificado".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="justificacion-text">Motivo de la justificación</Label>
              <Textarea
                id="justificacion-text"
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                placeholder="Describe el motivo de la justificación..."
                rows={4}
                data-testid="justificacion-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJustifyModal(false)} data-testid="cancel-justify">
              Cancelar
            </Button>
            <Button onClick={handleJustify} disabled={!justificacion.trim()} data-testid="confirm-justify">
              Justificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Baja Alumno Modal */}
      <Dialog open={showBajaModal} onOpenChange={setShowBajaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserMinus size={20} className="text-red-600" />
              Marcar como "Ya no asiste"
            </DialogTitle>
            <DialogDescription>
              {bajaAlumno && (
                <span>
                  Vas a dar de baja a <strong>{formatNombreCompleto(bajaAlumno)}</strong> ({bajaAlumno.matricula}).
                  El alumno dejará de aparecer en las listas de captura y ya NO se le contarán nuevas faltas. Se generará automáticamente un registro en el Expediente.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de baja</Label>
              <Select
                value={bajaForm.tipo_baja}
                onValueChange={(v) => setBajaForm((p) => ({ ...p, tipo_baja: v }))}
              >
                <SelectTrigger data-testid="tipo-baja-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja_temporal">Baja temporal (reversible)</SelectItem>
                  <SelectItem value="baja_definitiva">Baja definitiva</SelectItem>
                  <SelectItem value="traslado">Traslado a otra institución</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="baja-motivo">Motivo (obligatorio, mínimo 5 caracteres)</Label>
              <Textarea
                id="baja-motivo"
                value={bajaForm.motivo}
                onChange={(e) => setBajaForm((p) => ({ ...p, motivo: e.target.value }))}
                placeholder="Ej. Cambio de escuela, situación familiar, no se presentó durante 15 días hábiles, etc."
                rows={4}
                data-testid="baja-motivo-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBajaModal(false)} data-testid="cancel-baja" disabled={bajaLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleBaja}
              disabled={bajaLoading || !bajaForm.motivo.trim() || bajaForm.motivo.trim().length < 5}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-baja"
            >
              {bajaLoading ? 'Procesando...' : 'Confirmar baja'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AsistenciaPage;
